/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import {
	ResumeCitizensAttendancesIndicatorB1Data,
	getResumeCitizensAttendancesIndicatorB1,
} from "@/services/esus/indicator/esb/b1/getResumeCitizensAttendancesIndicatorB1";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorB1,
	ResumeIndicatorB1Data,
} from "@/services/esus/indicator/esb/b1/getResumeIndicatorB1";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorB1ByHealthTeam,
	ResumeIndicatorB1ByHealthTeamData,
} from "@/services/esus/indicator/esb/b1/getResumeIndicatorB1ByHealthTeam";

import { IndicatorEsbB1Resume } from "./IndicatorEsbB1Resume";
import { IndicatorEsbB1Attendances } from "./IndicatorEsbB1Attendances";
import { IndicatorEsbB1HealthTeams } from "./IndicatorEsbB1HealthTeams";

type IndicatorEsbB1Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsbB1({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsbB1Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorB1, setResumeIndicatorB1] = useState<ResumeIndicatorB1Data>(
		new ResumeIndicatorB1Data()
	);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchAttendances, setSearchAttendances] = useState("");
	const debouncedSearchAttendances = useDebounce(searchAttendances, 2000);
	const [isEvaluatedAttendances, setIsEvaluatedAttendances] = useState<boolean | undefined>(true);
	const [paginatedAttendances, setPaginatedAttendances] = useState(
		new Paginated<ResumeCitizensAttendancesIndicatorB1Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorB1ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorB1(),
			handleGetResumeIndicatorB1ByHealthTeam(),
			handleGetResumeCitizensAttendancesIndicatorB1(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorB1() {
		setResumeIndicatorB1(
			await getResumeIndicatorB1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorB1ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorB1ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorB1ByHealthTeam() {
		return (
			await getResumeIndicatorB1ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensAttendancesIndicatorB1(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedAttendances(
			await getResumeCitizensAttendancesIndicatorB1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchAttendances,
				isEvaluated: isEvaluatedAttendances,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllResumeCitizensAttendancesIndicatorB1() {
		return (
			await getResumeCitizensAttendancesIndicatorB1({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchAttendances,
				isEvaluated: isEvaluatedAttendances,
				pageNumber: 1,
				pageSize: paginatedAttendances.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeCitizensAttendancesIndicatorB1(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetResumeCitizensAttendancesIndicatorB1(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendances, isEvaluatedAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorB1ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorB1ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("B1 - Primeira Consulta Odontológica Programada");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsbB1Resume resume={resumeIndicatorB1} />
			<IndicatorEsbB1HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorB1ByHealthTeam={
					handleGetAllResumeIndicatorB1ByHealthTeam
				}
			/>
			<IndicatorEsbB1Attendances
				paginatedAttendances={paginatedAttendances}
				isLoading={isLoadingAttendances}
				search={searchAttendances}
				setSearch={setSearchAttendances}
				isEvaluated={isEvaluatedAttendances}
				setIsEvaluated={setIsEvaluatedAttendances}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllResumeCitizensAttendancesIndicatorB1={
					handleGetAllResumeCitizensAttendancesIndicatorB1
				}
			/>
		</>
	);
}
